/* file: src/hooks/useAgencyWorker.js */
import { useEffect, useRef, useState, useCallback } from 'react';

export default function useAgencyWorker({
  workerPath = '/agency.worker.js',
  uiHeartbeatMs = 1000, 
  session = { role: null, userId: null }
} = {}) {
  const workerRef = useRef(null);
  const hbTimerRef = useRef(null);
  const [alerts, setAlerts] = useState([]);
  const [connected, setConnected] = useState(false);
  const sessionRole = session?.role ?? null;
  const sessionUserId = session?.userId ?? null;

  // 1. INIT WORKER
  useEffect(() => {
    if (!('Worker' in window)) return;

    const w = new Worker(workerPath);
    workerRef.current = w;

    w.onmessage = (ev) => {
      const { type, payload } = ev.data || {};
      if (type === 'ALERTS_UPDATED') {
        setAlerts(Array.isArray(payload) ? payload : []);
      }
    };

    // Send Init Session
    w.postMessage({
      type: 'INIT_SESSION',
      payload: { role: sessionRole, userId: sessionUserId },
    });
    setConnected(true);

    return () => {
      w.terminate();
      workerRef.current = null;
      setConnected(false);
    };
  }, [workerPath, sessionRole, sessionUserId]); // Re-run only if session identity changes

  // 2. HEARTBEAT (Keep-Alive)
  useEffect(() => {
    if (!workerRef.current) return;
    
    hbTimerRef.current = setInterval(() => {
      workerRef.current?.postMessage({ type: 'HEARTBEAT' });
    }, uiHeartbeatMs);

    return () => clearInterval(hbTimerRef.current);
  }, [uiHeartbeatMs, connected]);

  // 3. EXPOSED FUNCTIONS
  const updateData = useCallback((data) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'UPDATE_DATA', payload: data });
    }
  }, []);

  return { alerts, updateData, connected };
}
